import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, ChevronDownIcon, GiftIcon, HelpCircleIcon, QuoteIcon, ShieldIcon, TrophyIcon, XIcon } from 'lucide-react';
import { bonuses, developedTestimonials, faqs, modules, pains, testimonials } from '../data/content';
import { Button } from './ui/Button';
const collapseTransition = {
  duration: 0.22,
  ease: [0.23, 1, 0.32, 1] as const
};
function Eyebrow({
  children,
  danger = false
}: {
  children: React.ReactNode;
  danger?: boolean;
}) {
  return <p className={`mb-3 text-center text-[11px] font-extrabold uppercase tracking-[.16em] md:text-sm ${danger ? 'text-red-500' : 'text-primary'}`} data-id="element-17" data-mp="1ir42qbsw3r2t">
      {children}
    </p>;
}
function SectionTitle({
  children
}: {
  children: React.ReactNode;
}) {
  return <h2 className="mx-auto max-w-3xl text-center text-2xl font-black leading-tight text-ink md:text-4xl" data-id="element-18" data-mp="2z2htxjt3yv69">
      {children}
    </h2>;
}
export function Testimonials() {
  return <section className="mx-auto max-w-6xl px-4 py-12 md:py-14" data-id="element-19" data-mp="1bom219iayy4r">
      <Eyebrow data-id="element-20" data-mp="16qkhek58aqr6">⭐ 4,9/5 · +150 profesionales</Eyebrow>
      <SectionTitle data-id="element-21" data-mp="16r3rxfsjucv6">
        Lo que dicen las que <span className="text-primary" data-id="element-22" data-mp="12oyz2j9xdk9l">ya lo están usando</span>
      </SectionTitle>
      <div className="mt-8 grid gap-4 md:grid-cols-3" data-id="element-23" data-mp="12qtmq5ckzqhd">
        {testimonials.map(item => <article key={item.name} className="relative flex flex-col rounded-2xl border border-line bg-white p-6 shadow-sm" data-id="element-24" data-mp="xs0aeg9165is">
            <QuoteIcon className="absolute right-5 top-5 h-7 w-7 text-primary/15" data-id="element-25" data-mp="nzaxri1qullv" />
            <div className="mb-3 text-sm text-gold" data-id="element-26" data-mp="j085ky6v49vb">⭐⭐⭐⭐⭐</div>
            <p className="mb-5 flex-1 text-base font-semibold italic leading-relaxed text-slate-700" data-id="element-27" data-mp="iync7bwaawz1">
              “{item.text}”
            </p>
            <div className="mt-auto flex items-center gap-3 border-t border-line pt-4" data-id="element-28" data-mp="9z9y25x7tk7z">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 border-primary/20 bg-canvas text-sm font-black text-primary" data-id="element-29" data-mp="50yo7v6x79pr">
                {item.initials}
              </span>
              <div data-id="element-30" data-mp="dh22gjekr6h">
                <p className="text-sm font-extrabold text-ink" data-id="element-31" data-mp="e0ittxbh9ix">{item.name}</p>
                <p className="text-xs text-slate-500" data-id="element-32" data-mp="3hm4aptea4wit">📍 {item.place}</p>
              </div>
            </div>
          </article>)}
      </div>
    </section>;
}
export function Pain() {
  return <section className="border-y border-line bg-white px-4 py-12 md:py-14" data-id="element-33" data-mp="e4m57slzc1lk">
      <div className="mx-auto max-w-4xl" data-id="element-34" data-mp="j25j2rermccz">
        <Eyebrow danger data-id="element-35" data-mp="j59ti6itq4c3">😤 La realidad</Eyebrow>
        <SectionTitle data-id="element-36" data-mp="4f2l6jmj61p4">
          ¿Cuántas clientas más vas a ver con{' '}
          <span className="text-primary" data-id="element-37" data-mp="9d45t8zlu92p">cicatrices innecesarias?</span>
        </SectionTitle>
        <div className="mt-8 grid gap-3 sm:grid-cols-2" data-id="element-38" data-mp="x2yhvoc3ntlw">
          {pains.map(pain => <div key={pain} className="flex items-start gap-3 rounded-xl border border-line bg-canvas p-4" data-id="element-39" data-mp="11tolox4vnoeh">
              <XIcon className="mt-0.5 h-5 w-5 shrink-0 text-red-500" data-id="element-40" data-mp="2mispf9o0bct9" />
              <span className="text-sm font-semibold text-slate-700 md:text-base" data-id="element-41" data-mp="2hlisl8yafyve">{pain}</span>
            </div>)}
        </div>
        <p className="mt-8 text-center text-lg font-bold text-slate-700 md:text-2xl" data-id="element-42" data-mp="2w9dlapcaq38h">
          Cada sesión es un “juego de azar”. No es falta de vocación.{' '}
          <span className="font-black text-primary" data-id="element-43" data-mp="23kpuyfcowno2">Es falta de un sistema.</span>
        </p>
        <div className="mt-7 text-center" data-id="element-44" data-mp="1ymemo921ml7y">
          <Button href="#pricing" className="w-full text-sm md:w-auto md:text-base" data-id="element-45" data-mp="1ynpow4fubkwy">
            SÍ, QUIERO PROTOCOLOS CLAROS →
          </Button>
        </div>
      </div>
    </section>;
}
export function ProductOverview() {
  const [open, setOpen] = useState(0);
  return <section className="mx-auto max-w-4xl px-4 py-12 md:py-14" data-id="element-46" data-mp="3o01kvwybn0k1">
      <Eyebrow data-id="element-47" data-mp="3jzhkmkigbgto">
        <span className="inline-flex items-center gap-2" data-id="element-48" data-mp="3k00v5g5rv2xo">
          <TrophyIcon className="h-4 w-4" data-id="element-49" data-mp="3k1kan4jlt9ye" /> Qué incluye el sistema
        </span>
      </Eyebrow>
      <SectionTitle data-id="element-50" data-mp="j6cwekq4xdep">
        5 módulos. <span className="text-primary" data-id="element-51" data-mp="n5lm9ei20r4z">Principiante → Avanzado.</span>
      </SectionTitle>
      <p className="mt-3 text-center font-semibold text-slate-600" data-id="element-52" data-mp="s4yd0ac1qicu">
        370+ páginas con protocolos, evaluaciones y extracciones listas para usar.
      </p>

      <div className="mt-8 space-y-3" data-id="element-53" data-mp="x5ck9q5tpo9t">
        {modules.map((mod, index) => <div key={mod.name} className={`overflow-hidden rounded-2xl border bg-white transition-colors duration-150 ease-out ${open === index ? 'border-primary shadow-lg shadow-emerald-100' : 'border-line'}`} data-id="element-54" data-mp="122cp5xqxskzc" data-mpi={index}>
            <button className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left md:px-6" aria-expanded={open === index} onClick={() => setOpen(open === index ? -1 : index)} data-id="element-55" data-mp="1fuj63m1wyvzu">
              <span className="flex items-center gap-3" data-id="element-56" data-mp="1pti5ee0shfa6">
                <span className="text-2xl" data-id="element-57" data-mp="3aar8fnqevsna">{mod.icon}</span>
                <span data-id="element-58" data-mp="3ac2fbwf5lgim">
                  <b className="block text-sm font-black uppercase text-ink md:text-base" data-id="element-59" data-mp="3a8em6ydmew4f">
                    {mod.name}
                  </b>
                  <small className="font-bold text-primary" data-id="element-60" data-mp="30dmn1q4e417j">{mod.period}</small>
                </span>
              </span>
              <ChevronDownIcon className={`h-5 w-5 shrink-0 text-primary transition-transform duration-200 ease-out ${open === index ? 'rotate-180' : ''}`} data-id="element-61" data-mp="2vf1l9m9hp9gz" />
            </button>
            <AnimatePresence initial={false} data-id="element-62" data-mp="3o2xrbhky8dqf">
              {open === index && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} transition={collapseTransition} data-id="element-63" data-mp="3ja4leqdquzcr">
                  <ul className="space-y-2 border-t border-line px-5 py-4 text-sm font-semibold text-slate-600 md:px-6" data-id="element-64" data-mp="avvxvwq2wyrg">
                    {mod.content.map(line => <li key={line} className="flex gap-2" data-id="element-65" data-mp="255u5dhcu3pqe">
                        <span className="text-primary" data-id="element-66" data-mp="2a19iu86xjjma">→</span>
                        {line}
                      </li>)}
                  </ul>
                </motion.div>}
            </AnimatePresence>
          </div>)}
      </div>

      <div className="mt-8 text-center" data-id="element-67" data-mp="1mcqkolhlgn7x">
        <Button href="#pricing" className="w-full text-sm md:w-auto md:text-base" data-id="element-68" data-mp="1mbov7bztzy6o">
          DESBLOQUEAR LOS 5 MÓDULOS HOY →
        </Button>
      </div>
    </section>;
}
export function BonusesSection() {
  return <section className="border-y border-line bg-white px-4 py-12 md:py-14" data-id="element-69" data-mp="1ch5bo3e3ymoo">
      <div className="mx-auto max-w-6xl" data-id="element-70" data-mp="1he5jojuipzif">
        <Eyebrow data-id="element-71" data-mp="36t3bjeqj267w">
          <span className="inline-flex items-center gap-2" data-id="element-72" data-mp="36sk4wu6bv496">
            <GiftIcon className="h-4 w-4" data-id="element-73" data-mp="36r0on3ko8t7e" /> 3 bonos siempre incluidos
          </span>
        </Eyebrow>
        <SectionTitle data-id="element-74" data-mp="104ksq19ent40">
          Además te llevás <span className="text-primary" data-id="element-75" data-mp="105mht9n9ag4q">3 bonos gratis</span>
        </SectionTitle>
        <div className="mt-8 grid gap-4 md:grid-cols-3" data-id="element-76" data-mp="v6012q87yhr6">
          {bonuses.map(([number, icon, name, description, value]) => <article key={number} className="flex flex-col rounded-2xl border border-line bg-canvas p-5 shadow-sm" data-id="element-77" data-mp="19vxlcv312nb3">
              <div className="flex gap-3" data-id="element-78" data-mp="1iusv2h5a6fj5">
                <span className="text-3xl" data-id="element-79" data-mp="1ithpqcw6szpx">{icon}</span>
                <div data-id="element-80" data-mp="1dwhi3xjovowp">
                  <small className="font-extrabold uppercase tracking-wide text-primary" data-id="element-81" data-mp="1duy2m95uxhvz">
                    Bono {number}
                  </small>
                  <h3 className="text-base font-black uppercase leading-tight text-ink" data-id="element-82" data-mp="1sqw3u5un1cf2">{name}</h3>
                </div>
              </div>
              <p className="my-4 flex-1 text-sm font-semibold leading-relaxed text-slate-600" data-id="element-83" data-mp="3ujtsrqlankuq">
                {description}
              </p>
              <div className="mt-auto flex items-center border-t border-line pt-4 text-sm font-extrabold" data-id="element-84" data-mp="3pnlgqm2e87rs">
                <span className="text-slate-400 line-through" data-id="element-85" data-mp="33q8os6rvdp4v">{value}</span>
                <span className="ml-auto rounded-full bg-primary/10 px-3 py-1 text-primary" data-id="element-86" data-mp="38pv5iq6wpnif">
                  GRATIS
                </span>
              </div>
            </article>)}
        </div>
        <p className="mt-7 text-center text-base font-bold text-slate-600 md:text-lg" data-id="element-87" data-mp="3igfzu1fjs5vj">
          Valor total de bonos: <s className="text-slate-400" data-id="element-88" data-mp="2kvlf8vwwr390">$38.000</s> →{' '}
          <span className="font-black text-primary" data-id="element-89" data-mp="2ptmwdbarwshu">incluidos gratis hoy</span>
        </p>
        <div className="mt-6 text-center" data-id="element-90" data-mp="2urexmvxb5qyp">
          <Button href="#pricing" className="w-full text-sm md:w-auto md:text-base" data-id="element-91" data-mp="2uqddm1ybdu4u">
            🎁 QUIERO LOS 3 BONOS GRATIS ANTES QUE CIERRE →
          </Button>
        </div>
      </div>
    </section>;
}
export function Results() {
  return <section className="mx-auto max-w-5xl px-4 py-12 md:py-14" data-id="element-92" data-mp="e2hstedawe3c">
      <Eyebrow data-id="element-93" data-mp="e4vo9obw7sbf">❤️ Resultados reales</Eyebrow>
      <SectionTitle data-id="element-94" data-mp="1ylbo271rlyb5">
        Profesionales que <span className="text-primary" data-id="element-95" data-mp="1yn671fth7gcl">transformaron sus sesiones</span>
      </SectionTitle>

      <div className="mt-8 space-y-4" data-id="element-96" data-mp="1uk9tw4mpgcg1">
        {developedTestimonials.map(item => <article key={item.name} className="rounded-2xl border border-line bg-white p-5 shadow-sm md:flex md:gap-6 md:p-6" data-id="element-97" data-mp="1pr70pxlub50d">
            <div className="flex items-center gap-3 md:w-60 md:shrink-0 md:flex-col md:items-start" data-id="element-98" data-mp="1fv3o1xtd3nz6">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-2 border-primary/20 bg-canvas text-sm font-black text-primary" data-id="element-99" data-mp="1av7tsqagvawt">
                {item.initials}
              </span>
              <div data-id="element-100" data-mp="11w49jizkcpgq">
                <p className="text-sm font-extrabold text-ink" data-id="element-101" data-mp="11wngy5rl7vgi">{item.name}</p>
                <p className="text-xs font-semibold leading-snug text-slate-500" data-id="element-102" data-mp="316lvqvuhnmsu">📍 {item.place}</p>
              </div>
            </div>
            <div className="mt-4 border-t border-line pt-4 md:mt-0 md:flex-1 md:border-l md:border-t-0 md:pl-6 md:pt-0" data-id="element-103" data-mp="2w6r7l023lbbh">
              <div className="flex flex-wrap items-center gap-3" data-id="element-104" data-mp="206bmamvr9pfb">
                <span className="text-sm text-gold" data-id="element-105" data-mp="2085x38dbc15o">⭐⭐⭐⭐⭐</span>
                <span className="rounded-full bg-primary/10 px-3 py-1 text-[11px] font-extrabold uppercase tracking-wide text-primary" data-id="element-106" data-mp="2e4rtsg1fzjyi">
                  {item.result}
                </span>
              </div>
              <p className="mt-3 text-sm font-semibold italic leading-relaxed text-slate-700" data-id="element-107" data-mp="1lhyp5mkxm0j8">
                “{item.text}”
              </p>
            </div>
          </article>)}
      </div>
    </section>;
}
export function Guarantee() {
  return <section className="mx-auto max-w-5xl px-4 pb-12 md:pb-14" data-id="element-108" data-mp="1qgi8rgc1z08f">
      <div className="flex flex-col items-center gap-5 rounded-3xl border-2 border-primary/20 bg-white p-6 text-center shadow-sm md:flex-row md:p-8 md:text-left" data-id="element-109" data-mp="1qe5lqorftzoi">
        <span className="inline-flex shrink-0 rounded-full border-2 border-primary bg-canvas p-4" data-id="element-110" data-mp="xqjf17mwyexf">
          <ShieldIcon className="h-9 w-9 text-primary" data-id="element-111" data-mp="39e4t5du7rjw3" />
        </span>
        <div data-id="element-112" data-mp="3e66edp9d0ky9">
          <h3 className="text-lg font-black uppercase text-ink md:text-xl" data-id="element-113" data-mp="3e5wp63w52htm">Garantía de 7 días</h3>
          <p className="mt-2 text-sm font-semibold leading-relaxed text-slate-600" data-id="element-114" data-mp="30f1b8d6s9aj6">
            Abrís los módulos y los usás en tus sesiones. Si no es lo que esperabas, escribís a
            compradigitalyaa@gmail.com y te devolvemos el 100%. Sin formularios, sin drama. Tasa de
            devoluciones: <b className="text-primary" data-id="element-115" data-mp="zee20utzt97">menos del 0,5%</b>.
          </p>
        </div>
      </div>
    </section>;
}
export function FAQSection() {
  const [open, setOpen] = useState(0);
  return <section className="border-t border-line bg-white px-4 py-12 md:py-14" data-id="element-116" data-mp="3o31bc3pzidaf">
      <div className="mx-auto max-w-3xl" data-id="element-117" data-mp="27pknvty0sjtt">
        <Eyebrow data-id="element-118" data-mp="27ot0h6ektsem">
          <span className="inline-flex items-center gap-2" data-id="element-119" data-mp="27o9fthxjkpww">
            <HelpCircleIcon className="h-4 w-4" data-id="element-120" data-mp="2coxhcxjecp5d" /> Preguntas frecuentes
          </span>
        </Eyebrow>
        <SectionTitle data-id="element-121" data-mp="1slhsgkh8pae1">
          Lo que más <span className="text-primary" data-id="element-122" data-mp="1slrho5ugndio">nos preguntan</span>
        </SectionTitle>
        <div className="mt-8 space-y-3" data-id="element-123" data-mp="1olhf735vn58k">
          {faqs.map(([question, answer], index) => <div key={question} className={`overflow-hidden rounded-2xl border bg-white transition-colors duration-150 ease-out ${open === index ? 'border-primary/50 shadow-lg shadow-emerald-100' : 'border-line'}`} data-id="element-124" data-mp="1jsxyvyft1w02" data-mpi={index}>
              <button className="flex w-full items-center justify-between gap-3 p-5 text-left text-sm font-extrabold text-ink md:text-base" onClick={() => setOpen(open === index ? -1 : index)} aria-expanded={open === index} data-id="element-125" data-mp="14x8ajtn5d6s6">
                {question}
                <ChevronDownIcon className={`h-5 w-5 shrink-0 text-primary transition-transform duration-200 ease-out ${open === index ? 'rotate-180' : ''}`} data-id="element-126" data-mp="2v9o2rawrabqi" />
              </button>
              <AnimatePresence initial={false} data-id="element-127" data-mp="2xt15sy93e6wu">
                {open === index && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={collapseTransition} className="border-t border-line px-5 py-4 text-sm font-semibold leading-relaxed text-slate-600" data-id="element-128" data-mp="32rvmnuni1leb">
                    {answer}
                  </motion.div>}
              </AnimatePresence>
            </div>)}
        </div>
        <div className="mt-9 rounded-2xl bg-canvas p-6 text-center" data-id="element-129" data-mp="2oruowo2j6u32">
          <p className="text-base font-black text-ink md:text-xl" data-id="element-130" data-mp="2sviuj17lrflo">
            El próximo protocolo puede ser diferente.
          </p>
          <p className="mt-1 text-sm font-semibold italic text-primary" data-id="element-131" data-mp="44zs5vxdch0m">
            “Finalmente sesiones con confianza profesional” 🧴
          </p>
          <div className="mt-5" data-id="element-132" data-mp="6mjs2dyawa3p">
            <Button href="#pricing" className="w-full text-sm md:w-auto md:text-base" data-id="element-133" data-mp="6nlefekroj0q">
              🧴 VER FACIAL PRO AHORA →
            </Button>
          </div>
          <p className="mt-3 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[11px] font-semibold text-slate-500" data-id="element-134" data-mp="glspxhwae8vc">
            <span className="flex items-center gap-1" data-id="element-135" data-mp="bkv2oqwf6omz">
              <CheckIcon className="h-3.5 w-3.5 text-primary" data-id="element-136" data-mp="plp7z0o689hf" /> Garantía 7 días
            </span>
            <span className="flex items-center gap-1" data-id="element-137" data-mp="knntyu5lr0ct">
              <CheckIcon className="h-3.5 w-3.5 text-primary" data-id="element-138" data-mp="kl1m0tesuiu2" /> Acceso en 5 minutos
            </span>
            <span className="flex items-center gap-1" data-id="element-139" data-mp="ze3cg16mvu7a">
              <CheckIcon className="h-3.5 w-3.5 text-primary" data-id="element-140" data-mp="udyv68xwivg0" /> Pago seguro
            </span>
          </p>
        </div>
      </div>
    </section>;
}